import api from '@/lib/api';
import type { ApiResponse, PaginationMeta } from '@amuster/types';

// ── Types ───────────────────────────────────────────────────────────────────

// Dashboard
export interface ClinicDashboardData {
  today_visits: number;
  active_visits: number;
  pending_follow_ups: number;
  low_stock_count: number;
  expiring_medicines_count: number;
  equipment_checks_due: number;
  recent_visits: ClinicVisit[];
  low_stock_items: InventoryItem[];
  expiring_medicines: InventoryItem[];
  equipment_due: MedicalEquipment[];
}

// Visits
export interface ClinicVisit {
  id: string;
  tenant_id: string;
  visit_number: string;
  clinic_id: string;
  patient_type: string;
  student_id: string | null;
  user_id: string | null;
  visitor_id: string | null;
  patient_name: string | null;
  patient_class: string | null;
  patient_age: number | null;
  visit_date: string;
  visit_time: string;
  departure_time: string | null;
  visit_type: string;
  chief_complaint: string;
  is_incident_related: boolean;
  vital_signs: Record<string, unknown> | null;
  symptoms: string[] | null;
  physical_examination: string | null;
  assessment: string | null;
  treatment_provided: string | null;
  first_aid_given: boolean;
  first_aid_details: string | null;
  medication_administered: Array<{ medication: string; dosage: string; time: string; route: string }> | null;
  disposition: string | null;
  follow_up_required: boolean;
  follow_up_instructions: string | null;
  follow_up_date: string | null;
  parent_contacted: boolean;
  parent_contact_time: string | null;
  parent_contact_method: string | null;
  parent_contact_notes: string | null;
  referred_to_hospital: boolean;
  hospital_name: string | null;
  ambulance_called: boolean;
  ambulance_arrival_time: string | null;
  attending_nurse_id: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  student_name?: string;
  student_number?: string;
  attending_nurse_name?: string;
}

export interface CreateVisitRequest {
  clinic_id?: string;
  patient_type: string;
  student_id?: string;
  user_id?: string;
  visitor_id?: string;
  patient_name?: string;
  patient_class?: string;
  patient_age?: number;
  visit_type: string;
  chief_complaint: string;
  is_incident_related?: boolean;
  vital_signs?: Record<string, unknown>;
  symptoms?: string[];
  physical_examination?: string;
  assessment?: string;
  treatment_provided?: string;
  first_aid_given?: boolean;
  first_aid_details?: string;
  medication_administered?: Array<{ medication: string; dosage: string; time: string; route: string }>;
  disposition?: string;
  follow_up_required?: boolean;
  follow_up_instructions?: string;
  follow_up_date?: string;
  parent_contacted?: boolean;
  parent_contact_method?: string;
  parent_contact_notes?: string;
  referred_to_hospital?: boolean;
  hospital_name?: string;
  ambulance_called?: boolean;
  notes?: string;
}

export interface VisitListQuery {
  page?: number;
  limit?: number;
  clinic_id?: string;
  patient_type?: string;
  visit_type?: string;
  disposition?: string;
  date_from?: string;
  date_to?: string;
  student_id?: string;
  search?: string;
}

// Medical Profiles
export interface MedicalProfile {
  id: string;
  tenant_id: string;
  student_id: string;
  arabic_name: string | null;
  religion: string | null;
  marital_status: string | null;
  sponsor_details: string | null;
  residency_country: string | null;
  area: string | null;
  p_o_box: string | null;
  blood_type: string | null;
  has_chronic_condition: boolean;
  chronic_conditions: string[] | null;
  has_allergies: boolean;
  food_allergies: string[] | null;
  drug_allergies: string[] | null;
  environmental_allergies: string[] | null;
  allergy_severity: string | null;
  allergy_action_required: string | null;
  has_asthma: boolean;
  has_diabetes: boolean;
  has_epilepsy: boolean;
  has_heart_condition: boolean;
  has_mobility_issues: boolean;
  current_medications: unknown;
  medications_at_school: boolean;
  emergency_contact_1_name: string | null;
  emergency_contact_1_phone: string | null;
  emergency_contact_1_relationship: string | null;
  emergency_contact_1_email: string | null;
  emergency_contact_1_employer: string | null;
  emergency_contact_2_name: string | null;
  emergency_contact_2_phone: string | null;
  emergency_contact_2_relationship: string | null;
  emergency_contact_2_email: string | null;
  emergency_contact_2_employer: string | null;
  primary_doctor_name: string | null;
  primary_doctor_phone: string | null;
  primary_doctor_clinic: string | null;
  insurance_provider: string | null;
  insurance_policy_number: string | null;
  insurance_expiry_date: string | null;
  consent_to_treat: boolean;
  consent_date: string | null;
  consent_by: string | null;
  consent_relationship: string | null;
  last_health_form_date: string | null;
  health_form_verified_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  student_name?: string;
  student_number?: string;
  grade_level?: string;
  section?: string;
}

export interface CreateMedicalProfileRequest {
  student_id: string;
  arabic_name?: string;
  religion?: string;
  marital_status?: string;
  sponsor_details?: string;
  residency_country?: string;
  area?: string;
  p_o_box?: string;
  blood_type?: string;
  has_chronic_condition?: boolean;
  chronic_conditions?: string[];
  has_allergies?: boolean;
  food_allergies?: string[];
  drug_allergies?: string[];
  environmental_allergies?: string[];
  allergy_severity?: string;
  allergy_action_required?: string;
  has_asthma?: boolean;
  has_diabetes?: boolean;
  has_epilepsy?: boolean;
  has_heart_condition?: boolean;
  has_mobility_issues?: boolean;
  current_medications?: unknown;
  medications_at_school?: boolean;
  emergency_contact_1_name?: string;
  emergency_contact_1_phone?: string;
  emergency_contact_1_relationship?: string;
  emergency_contact_1_email?: string;
  emergency_contact_1_employer?: string;
  emergency_contact_2_name?: string;
  emergency_contact_2_phone?: string;
  emergency_contact_2_relationship?: string;
  emergency_contact_2_email?: string;
  emergency_contact_2_employer?: string;
  doctor_name?: string;
  doctor_phone?: string;
  doctor_clinic?: string;
  insurance_provider?: string;
  insurance_policy_number?: string;
  consent_medical_treatment?: boolean;
  consent_medication_admin?: boolean;
  consent_emergency_transport?: boolean;
  consent_date?: string;
  consent_given_by?: string;
}

export interface ProfileListQuery {
  page?: number;
  limit?: number;
  has_allergies?: boolean;
  has_asthma?: boolean;
  has_chronic_condition?: boolean;
  grade_level?: string;
  search?: string;
}

// Health Action Plans
export interface HealthActionPlan {
  id: string;
  tenant_id: string;
  medical_profile_id: string;
  student_id: string;
  condition_type: string;
  condition_details: string;
  severity: string | null;
  known_triggers: string[] | null;
  warning_signs: string[] | null;
  symptoms: string[] | null;
  daily_management: string | null;
  emergency_treatment: string | null;
  medication_protocol: unknown;
  equipment_needed: string[] | null;
  equipment_location: string | null;
  when_to_call_emergency: string | null;
  special_instructions: string | null;
  pe_restrictions: string | null;
  activity_restrictions: string[] | null;
  food_restrictions: string[] | null;
  other_accommodations: string | null;
  doctor_authorization_date: string | null;
  doctor_name: string | null;
  doctor_signature_on_file: boolean;
  parent_acknowledgement: boolean;
  parent_acknowledgement_date: string | null;
  plan_start_date: string;
  plan_review_date: string | null;
  last_reviewed_date: string | null;
  reviewed_by: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateHealthActionPlanRequest {
  medical_profile_id: string;
  student_id: string;
  condition_type: string;
  condition_details: string;
  severity?: string;
  known_triggers?: string[];
  warning_signs?: string[];
  symptoms?: string[];
  daily_management?: string;
  emergency_treatment?: string;
  medication_protocol?: unknown;
  equipment_needed?: string[];
  equipment_location?: string;
  when_to_call_emergency?: string;
  special_instructions?: string;
  pe_restrictions?: string;
  activity_restrictions?: string[];
  food_restrictions?: string[];
  other_accommodations?: string;
  doctor_authorization_date?: string;
  doctor_name?: string;
  doctor_signature_on_file?: boolean;
  parent_acknowledgement?: boolean;
  parent_acknowledgement_date?: string;
  plan_start_date: string;
  plan_review_date?: string;
}

// Medication Administration
export interface MedicationAdministration {
  id: string;
  tenant_id: string;
  student_id: string;
  clinic_id: string;
  medication_name: string;
  dosage: string;
  route: string | null;
  frequency: string | null;
  scheduled_time: string | null;
  actual_time: string;
  administration_date: string;
  administered_by: string;
  verified_by: string | null;
  verification_time: string | null;
  patient_response: string | null;
  adverse_reaction: boolean;
  reaction_details: string | null;
  parent_authorization: boolean;
  authorization_date: string | null;
  doctor_order_on_file: boolean;
  notes: string | null;
  created_at: string;
  // Joined fields
  student_name?: string;
  administered_by_name?: string;
  verified_by_name?: string;
}

export interface CreateMedicationRequest {
  student_id: string;
  clinic_id?: string;
  medication_name: string;
  dosage: string;
  route?: string;
  frequency?: string;
  scheduled_time?: string;
  actual_time: string;
  administration_date: string;
  verified_by?: string;
  patient_response?: string;
  adverse_reaction?: boolean;
  reaction_details?: string;
  parent_authorization?: boolean;
  authorization_date?: string;
  doctor_order_on_file?: boolean;
  notes?: string;
}

export interface MedicationListQuery {
  page?: number;
  limit?: number;
  student_id?: string;
  clinic_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

// Lookup
export interface Icd10Entry {
  code: string;
  display: string;
  category: string;
}

export interface LoincEntry {
  code: string;
  display: string;
  category: string;
  default_unit: string | null;
  value_type: string;
}

// Inventory
export interface InventoryItem {
  id: string;
  tenant_id: string;
  clinic_id: string;
  medicine_name: string;
  generic_name: string | null;
  category: string | null;
  form: string | null;
  strength: string | null;
  quantity_in_stock: number;
  unit: string | null;
  minimum_stock_level: number;
  reorder_point: number;
  batch_number: string | null;
  expiry_date: string;
  storage_location: string | null;
  requires_refrigeration: boolean;
  supplier_name: string | null;
  last_purchase_date: string | null;
  last_purchase_price: number | null;
  photo_url: string | null;
  usage_restrictions: string | null;
  warnings_precautions: string | null;
  age_category: string | null;
  possible_side_effects: string | null;
  manufacturer: string | null;
  allowed_routes: string[] | null;
  vaccine_cvx_code: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Computed
  days_until_expiry?: number;
}

export interface CreateInventoryItemRequest {
  clinic_id?: string;
  medicine_name: string;
  generic_name?: string;
  category?: string;
  form?: string;
  strength?: string;
  quantity_in_stock: number;
  unit?: string;
  minimum_stock_level?: number;
  reorder_point?: number;
  batch_number?: string;
  expiry_date: string;
  storage_location?: string;
  requires_refrigeration?: boolean;
  supplier_name?: string;
  last_purchase_date?: string;
  last_purchase_price?: number;
  photo_url?: string;
  usage_restrictions?: string;
  warnings_precautions?: string;
  age_category?: string;
  possible_side_effects?: string;
  manufacturer?: string;
}

export interface BulkUploadResult {
  total_rows: number;
  inserted: number;
  validation_errors: Array<{ row: number; error: string }>;
  db_errors: Array<{ row: number; error: string }>;
}

export interface InventoryListQuery {
  page?: number;
  limit?: number;
  clinic_id?: string;
  search?: string;
  low_stock_only?: boolean;
  expiring_soon?: boolean;
}

// Inventory Transactions
export interface InventoryTransaction {
  id: string;
  tenant_id: string;
  medicine_id: string;
  clinic_id: string;
  transaction_type: string;
  quantity: number;
  transaction_date: string;
  reference_type: string | null;
  reference_id: string | null;
  received_from: string | null;
  dispensed_to: string | null;
  reason: string | null;
  recorded_by: string;
  created_at: string;
  // Joined fields
  medicine_name?: string;
  recorded_by_name?: string;
}

export interface CreateTransactionRequest {
  medicine_id: string;
  clinic_id: string;
  transaction_type: string;
  quantity: number;
  transaction_date: string;
  reference_type?: string;
  reference_id?: string;
  received_from?: string;
  dispensed_to?: string;
  reason?: string;
}

export interface TransactionListQuery {
  page?: number;
  limit?: number;
  clinic_id?: string;
  medicine_id?: string;
  transaction_type?: string;
  date_from?: string;
  date_to?: string;
}

// Equipment
export interface MedicalEquipment {
  id: string;
  tenant_id: string;
  clinic_id: string;
  equipment_type: string;
  name: string;
  serial_number: string | null;
  manufacturer: string | null;
  model: string | null;
  location_description: string | null;
  requires_calibration: boolean;
  calibration_frequency_months: number | null;
  last_calibration_date: string | null;
  next_calibration_date: string | null;
  calibration_provider: string | null;
  status: string;
  purchase_date: string | null;
  warranty_expiry_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Clinic ↔ EAM Integration Pack PR 2 — read integration fields.
  // Populated by LEFT JOIN to asset.assets when asset_id IS NOT NULL.
  // When is_eam_linked is true, asset_* fields carry the SoR values.
  asset_id?: string | null;
  is_eam_linked?: boolean;
  asset_name?: string | null;
  asset_code?: string | null;
  asset_serial_number?: string | null;
  asset_manufacturer?: string | null;
  asset_model?: string | null;
  asset_status?: string | null;
  asset_criticality?: string | null;
  asset_qr_code?: string | null;
  // PR 4A: deployment state + assignment drift (read-time derived)
  is_deployed?: boolean;
  has_assignment_drift?: boolean;
  // PR #501 (4A strengthen): canonical lifecycle state computed read-time.
  // 'standalone' when asset_id IS NULL; otherwise mirrors asset.assets.status.
  // Loose string union — backend vocabulary is the source of truth.
  effective_lifecycle_state?:
    | 'standalone'
    | 'draft'
    | 'active'
    | 'inactive'
    | 'under_maintenance'
    | 'out_of_service'
    | 'disposed'
    | 'transferred'
    | (string & {});
}

// Clinic ↔ EAM Integration Pack PR 3B — eligible asset for linking
export interface EligibleAssetRow {
  id: string;
  name: string;
  asset_code: string;
  serial_number: string | null;
  manufacturer: string | null;
  model: string | null;
  status: string;
  criticality: string;
  category_id: string | null;
  asset_type_id: string | null;
  qr_code: string;
}

// Clinic ↔ EAM Integration Pack PR 3C — create-linked request type.
// Matches the backend createLinkedEquipmentSchema from PR 3A.
export interface CreateLinkedEquipmentRequest {
  clinic_id: string;
  name: string;
  asset_type_id: string;           // REQUIRED — EAM classification
  category_id?: string;            // optional — backend derives from type if omitted
  serial_number?: string;
  manufacturer?: string;
  model?: string;
  criticality?: 'critical' | 'high' | 'medium' | 'low' | 'negligible';
  asset_status?: 'draft' | 'active';
  equipment_type?: string;
  requires_calibration?: boolean;
  calibration_frequency_months?: number;
  next_calibration_date?: string;
  notes?: string;
}

export interface CreateEquipmentRequest {
  clinic_id?: string;
  equipment_type: string;
  name: string;
  serial_number?: string;
  manufacturer?: string;
  model?: string;
  location_description?: string;
  requires_calibration?: boolean;
  calibration_frequency_months?: number;
  last_calibration_date?: string;
  next_calibration_date?: string;
  calibration_provider?: string;
  status?: string;
  purchase_date?: string;
  warranty_expiry_date?: string;
  notes?: string;
}

export interface EquipmentListQuery {
  page?: number;
  limit?: number;
  clinic_id?: string;
  status?: string;
  search?: string;
}

// Equipment Checks
export interface EquipmentCheck {
  id: string;
  tenant_id: string;
  equipment_id: string;
  clinic_id: string;
  check_date: string;
  check_type: string | null;
  checked_by: string;
  check_items: Record<string, unknown>;
  is_passed: boolean;
  issues_found: string | null;
  action_taken: string | null;
  created_at: string;
  // Joined fields
  equipment_name?: string;
  checked_by_name?: string;
}

export interface CreateCheckRequest {
  equipment_id: string;
  clinic_id?: string;
  check_date: string;
  check_type?: string;
  check_items: Record<string, unknown>;
  is_passed: boolean;
  issues_found?: string;
  action_taken?: string;
}

export interface CheckListQuery {
  page?: number;
  limit?: number;
  clinic_id?: string;
  equipment_id?: string;
  check_type?: string;
  date_from?: string;
  date_to?: string;
}

// Waste Collections
export interface WasteCollection {
  id: string;
  tenant_id: string;
  clinic_id: string;
  collection_date: string;
  collection_time: string | null;
  waste_type: string;
  quantity: number;
  unit: string;
  collector_company: string;
  collector_name: string | null;
  collector_id_number: string | null;
  manifest_number: string | null;
  certificate_received: boolean;
  certificate_date: string | null;
  recorded_by: string;
  notes: string | null;
  created_at: string;
  // Joined fields
  recorded_by_name?: string;
}

export interface CreateWasteRequest {
  clinic_id?: string;
  collection_date: string;
  collection_time?: string;
  waste_type: string;
  quantity: number;
  unit: string;
  collector_company: string;
  collector_name?: string;
  collector_id_number?: string;
  manifest_number?: string;
  certificate_received?: boolean;
  certificate_date?: string;
  notes?: string;
}

export interface WasteListQuery {
  page?: number;
  limit?: number;
  clinic_id?: string;
  date_from?: string;
  date_to?: string;
}

// Daily Patient Log
export interface DailyLogQuery {
  clinic_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface DailyLogEntry {
  id: string;
  visit_date: string;
  visit_time: string;
  patient_name: string | null;
  patient_class: string | null;
  patient_type: string;
  visit_type: string;
  chief_complaint: string | null;
  treatment_provided: string | null;
  disposition: string | null;
  attending_nurse_name: string | null;
}

// Activity Summaries
export interface ActivitySummary {
  id: string;
  tenant_id: string;
  clinic_id: string;
  report_month: number;
  report_year: number;
  total_visits: number;
  student_visits: number;
  staff_visits: number;
  visitor_visits: number;
  illness_visits: number;
  injury_visits: number;
  first_aid_visits: number;
  medication_visits: number;
  emergency_visits: number;
  returned_to_class: number;
  sent_home: number;
  referred_to_hospital: number;
  ambulance_calls: number;
  screenings_conducted: number;
  medications_administered: number;
  prepared_by: string | null;
  prepared_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  notes: string | null;
  created_at: string;
  // Joined fields
  prepared_by_name?: string;
  approved_by_name?: string;
}

export interface SummaryListQuery {
  page?: number;
  limit?: number;
  clinic_id?: string;
  year?: number;
}

export interface GenerateReportRequest {
  clinic_id: string;
  month: number;
  year: number;
}

// HIE Submission
export interface HieSubmissionResult {
  submitted: boolean;
  blocked_reason?: string;
  submission_id?: string;
}

// Clinic List (from GET /clinic/clinics)
export interface ClinicListItem {
  id: string;
  name: string;
  campus_id: string | null;
  campus_name: string | null;
  zone_id: string | null;
  zone_name: string | null;
  building_id: string | null;
  building_name: string | null;
  phone: string | null;
  email: string | null;
  opening_time: string | null;
  closing_time: string | null;
}

// ══════════════════════════════════════════════════════════════════════════════
// SoR Types (Phase 1+)
// ══════════════════════════════════════════════════════════════════════════════

// ── Encounters ──────────────────────────────────────────────────────────────

export interface ClinicEncounter {
  id: string;
  tenant_id: string;
  clinic_id: string;
  patient_id: string;
  encounter_number: string;
  encounter_class: string;
  status: string;
  chief_complaint: string;
  clinician_id: string;
  triage_acuity: string | null;
  triage_notes: string | null;
  triage_at: string | null;
  triage_by: string | null;
  building_id: string | null;
  zone_id: string | null;
  exact_location: string | null;
  disposition: string | null;
  disposition_note: string | null;
  follow_up_required: boolean;
  follow_up_date: string | null;
  follow_up_instructions: string | null;
  parent_contacted: boolean;
  parent_contact_method: string | null;
  is_incident_related: boolean;
  incident_id: string | null;
  started_at: string;
  ended_at: string | null;
  created_at: string;
}

export interface Observation {
  id: string;
  observation_code: string;
  observation_code_system: string;
  observation_display: string;
  value_numeric: number | null;
  value_text: string | null;
  value_coded: string | null;
  unit: string | null;
  is_abnormal: boolean;
  recorded_by: string;
  created_at: string;
}

export interface Condition {
  id: string;
  condition_code: string;
  condition_code_system: string;
  condition_display: string;
  clinical_status: string;
  onset_date: string | null;
  severity: string | null;
  clinical_notes: string | null;
  recorded_by: string;
  created_at: string;
}

export interface SorMedicationAdmin {
  id: string;
  medication_name: string;
  medication_code: string | null;
  dosage: string | null;
  dosage_numeric: number | null;
  dosage_unit: string | null;
  route: string;
  lot_number: string | null;
  administered_by: string;
  allergy_check_passed: boolean;
  allergy_override_reason: string | null;
  allergy_override_is_break_glass: boolean;
  high_risk_confirmed: boolean;
  dose_range_check: string | null;
  witness_user_id: string | null;
  created_at: string;
}

export interface Immunization {
  id: string;
  vaccine_code: string;
  vaccine_code_system: string;
  vaccine_display: string;
  lot_number: string | null;
  manufacturer: string | null;
  site: string | null;
  route: string | null;
  dose_number: number | null;
  administered_by: string;
  created_at: string;
}

export interface Referral {
  id: string;
  referral_type: string;
  urgency: string;
  referred_to: string;
  reason: string;
  status: string;
  outcome_notes: string | null;
  issued_by: string;
  created_at: string;
}

export interface ClinicEncounterDetail extends ClinicEncounter {
  observations: Observation[];
  conditions: Condition[];
  medications: SorMedicationAdmin[];
  immunizations: Immunization[];
  referrals: Referral[];
  server_now: string;
}

export interface EncounterListQuery {
  page?: number;
  limit?: number;
  clinic_id?: string;
  patient_id?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
}

export interface CreateEncounterRequest {
  clinic_id: string;
  patient_id: string;
  encounter_class: string;
  chief_complaint: string;
  clinician_id?: string;
  triage_acuity?: string;
  triage_notes?: string;
  building_id?: string;
  zone_id?: string;
  exact_location?: string;
}

export interface TriageEncounterRequest {
  triage_acuity: string;
  triage_notes?: string;
}

export interface CloseEncounterRequest {
  disposition: string;
  disposition_note?: string;
  follow_up_required?: boolean;
  follow_up_date?: string;
  follow_up_instructions?: string;
  parent_contacted?: boolean;
  parent_contact_method?: string;
}

export interface RecordVitalsBatchRequest {
  vitals: Array<{
    observation_code: string;
    observation_display?: string;
    value_numeric: number;
    unit: string;
  }>;
}

// ── Clinical Data Requests ──────────────────────────────────────────────────

export interface CreateObservationRequest {
  encounter_id: string;
  patient_id: string;
  observation_code: string;
  observation_code_system?: string;
  observation_display: string;
  value_numeric?: number;
  value_text?: string;
  value_coded?: string;
  unit?: string;
  is_abnormal?: boolean;
  observed_at?: string;
}

export interface CreateConditionRequest {
  encounter_id: string;
  patient_id: string;
  condition_code: string;
  condition_code_system?: string;
  condition_display: string;
  clinical_status?: string;
  onset_date?: string;
  severity?: string;
  clinical_notes?: string;
}

export interface AdministerMedicationRequest {
  encounter_id?: string;
  patient_id: string;
  medication_code?: string;
  medication_code_system?: string;
  medication_display?: string;
  dosage_value?: number;
  dosage_unit?: string;
  route: string;
  administered_at: string;
  scheduled_at?: string;
  time_window_minutes?: number;
  is_break_glass?: boolean;
  allergy_override_reason?: string;
  high_risk_confirmed?: boolean;
  witness_user_id?: string;
  // Inventory integration
  medicine_inventory_id?: string;
  quantity?: number;
}

export interface SafetyCheckRequest {
  encounter_id?: string;
  patient_id: string;
  medication_code?: string;
  medication_name: string;
  dosage_numeric?: number;
  dosage_unit?: string;
  route?: string;
  scheduled_at?: string;
  administered_at?: string;
}

export interface SafetyCheckResult {
  allergy_check: {
    has_allergy: boolean;
    matching_allergies: Array<{ substance: string; severity: string }>;
  };
  dose_range_check: {
    status: string;
    expected_min?: number;
    expected_max?: number;
    actual?: number;
  };
  route_check: {
    status: string;
    allowed_routes?: string[];
  };
  time_check: {
    status: string;
    scheduled_at?: string;
    administered_at?: string;
  };
  right_patient: {
    verified: boolean;
  };
  can_proceed: boolean;
  override_reasons: string[];
}

export interface AdministerImmunizationRequest {
  encounter_id: string;
  patient_id: string;
  vaccine_code: string;
  vaccine_code_system?: string;
  vaccine_display: string;
  lot_number?: string;
  manufacturer?: string;
  expiry_date?: string;
  dose_number?: number;
  administered_at: string;
  site?: string;
  route?: string;
  // Inventory integration
  medicine_inventory_id?: string;
  quantity?: number;
}

export interface CreateReferralRequest {
  encounter_id: string;
  patient_id: string;
  referral_type: string;
  urgency?: string;
  referred_to: string;
  reason: string;
  notes?: string;
}

export interface UpdateReferralRequest {
  status: string;
  outcome_notes?: string;
  notes?: string;
}

export interface CreateConsentRequest {
  patient_id: string;
  consent_type: string;
  status?: string;
  sharing_target?: string;
  granted_by?: string;
  expires_at?: string;
}

export interface RevokeConsentRequest {
  reason: string;
}

// ── Incident Link ───────────────────────────────────────────────────────────

export interface CreateIncidentFromEncounterRequest {
  building_id: string;
  zone_id?: string;
  severity: string;
}

export interface LinkEncounterToIncidentRequest {
  incident_id: string;
}

// ── Auto-Muster Alerts ──────────────────────────────────────────────────────

export interface AutoMusterAlert {
  id: string;
  tenant_id: string;
  encounter_id: string;
  building_id: string;
  triage_acuity: string;
  state: string;
  triggered_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  false_alarm_reason: string | null;
  false_alarm_notes: string | null;
  incident_id: string | null;
  muster_session_id: string | null;
}

export interface AutoMusterAlertListQuery {
  page?: number;
  limit?: number;
  state?: string;
  date_from?: string;
  date_to?: string;
}

export interface ResolveAlertRequest {
  resolution_notes?: string;
}

export interface MarkFalseAlarmRequest {
  reason_code: string;
  notes?: string;
}

// ── Appointments ─────────────────────────────────────────────────────────────

export interface Appointment {
  id: string;
  tenant_id: string;
  clinic_id: string;
  patient_id: string;
  patient_name: string | null;
  clinician_id: string | null;
  clinician_name: string | null;
  appointment_type: string;
  scheduled_start_at: string;
  scheduled_end_at: string;
  room: string | null;
  status: string;
  reason: string | null;
  notes: string | null;
  cancellation_reason: string | null;
  encounter_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AppointmentListQuery {
  page?: number;
  limit?: number;
  clinic_id?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
}

export interface CreateAppointmentRequest {
  clinic_id: string;
  patient_id: string;
  clinician_id?: string;
  appointment_type: string;
  scheduled_start: { localDateTime: string; zone: string };
  scheduled_end: { localDateTime: string; zone: string };
  room?: string;
  reason?: string;
  notes?: string;
}

// ── Queue ────────────────────────────────────────────────────────────────────

export interface QueueEntry {
  encounter_id: string;
  encounter_number: string;
  patient_id: string;
  patient_name: string | null;
  person_type: string;
  chief_complaint: string;
  triage_acuity: string | null;
  triage_at: string | null;
  status: string;
  minutes_since_arrival: number;
  minutes_since_triage: number;
}

export interface QueueListQuery {
  clinic_id?: string;
}

// ── Create Clinic ────────────────────────────────────────────────────────────

export interface CreateClinicRequest {
  name: string;
  campus_id: string;
  zone_id?: string;
  phone?: string;
  email?: string;
  opening_time?: string;
  closing_time?: string;
}

export interface UpdateClinicRequest {
  name: string;
  zone_id: string;
  phone?: string;
  email?: string;
  opening_time?: string;
  closing_time?: string;
}

// ── Staff ───────────────────────────────────────────────────────────────────

export interface ClinicStaffMember {
  id: string;
  clinic_id: string;
  user_id: string;
  staff_role: string;
  is_active: boolean;
  assigned_at: string;
  updated_at: string;
  user_name: string;
  user_email: string;
}

// ── Staff Documents ─────────────────────────────────────────────────────────

export interface StaffDocumentType {
  id: string;
  code: string;
  label: string;
  sort_order: number;
  requires_expiry: boolean;
}

export type StaffDocVerificationStatus = 'pending' | 'approved' | 'rejected';
export type StaffDocAvScanStatus = 'pending' | 'clean' | 'quarantined';

export interface StaffDocument {
  id: string;
  tenant_id: string;
  clinic_staff_id: string;
  document_type_id: string;
  document_type_code: string;
  document_type_label: string;
  storage_key: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  content_hash: string;
  valid_until: string | null;
  verification_status: StaffDocVerificationStatus;
  verified_by: string | null;
  verified_at: string | null;
  rejection_reason: string | null;
  is_current: boolean;
  av_scan_status: StaffDocAvScanStatus;
  av_scanned_at: string | null;
  uploaded_by: string;
  deleted_at: string | null;
  deleted_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface UploadStaffDocumentRequest {
  document_type_code: string;
  valid_until?: string;
  file: File;
}

export interface VerifyStaffDocumentRequest {
  verification_status: 'approved' | 'rejected';
  rejection_reason?: string;
  valid_until?: string;
}

// ── Service ─────────────────────────────────────────────────────────────────

export const clinicService = {
  // ── Clinics (scope bootstrap) ─────────────────────────────────────────────

  listClinics() {
    return api.get<ApiResponse<ClinicListItem[]>>('/clinic/clinics');
  },

  // ── Dashboard ──────────────────────────────────────────────────────────────

  getDashboard(clinicId?: string) {
    return api.get<ApiResponse<ClinicDashboardData>>('/clinic/dashboard', {
      params: clinicId ? { clinic_id: clinicId } : undefined,
    });
  },

  // ── Visits ─────────────────────────────────────────────────────────────────

  listVisits(query?: VisitListQuery) {
    return api.get<ApiResponse<ClinicVisit[]> & { meta: PaginationMeta }>(
      '/clinic/visits',
      { params: query },
    );
  },

  getVisit(id: string) {
    return api.get<ApiResponse<ClinicVisit>>(`/clinic/visits/${id}`);
  },

  createVisit(data: CreateVisitRequest) {
    return api.post<ApiResponse<ClinicVisit>>('/clinic/visits', data);
  },

  updateVisit(id: string, data: Partial<CreateVisitRequest>) {
    return api.put<ApiResponse<ClinicVisit>>(`/clinic/visits/${id}`, data);
  },

  // ── Medical Profiles ───────────────────────────────────────────────────────

  listProfiles(query?: ProfileListQuery) {
    return api.get<ApiResponse<MedicalProfile[]> & { meta: PaginationMeta }>(
      '/clinic/medical-profiles',
      { params: query },
    );
  },

  getProfile(id: string) {
    return api.get<ApiResponse<MedicalProfile & { action_plans: HealthActionPlan[] }>>(
      `/clinic/medical-profiles/${id}`,
    );
  },

  createProfile(data: CreateMedicalProfileRequest) {
    return api.post<ApiResponse<MedicalProfile>>('/clinic/medical-profiles', data);
  },

  updateProfile(id: string, data: Partial<CreateMedicalProfileRequest>) {
    return api.put<ApiResponse<MedicalProfile>>(`/clinic/medical-profiles/${id}`, data);
  },

  // ── Health Action Plans ────────────────────────────────────────────────────

  listActionPlans(profileId: string) {
    return api.get<ApiResponse<HealthActionPlan[]>>(
      `/clinic/medical-profiles/${profileId}/action-plans`,
    );
  },

  createActionPlan(data: CreateHealthActionPlanRequest) {
    return api.post<ApiResponse<HealthActionPlan>>('/clinic/health-action-plans', data);
  },

  updateActionPlan(id: string, data: Partial<CreateHealthActionPlanRequest>) {
    return api.put<ApiResponse<HealthActionPlan>>(`/clinic/health-action-plans/${id}`, data);
  },

  // ── Student History ────────────────────────────────────────────────────────

  getStudentVisits(studentId: string, query?: { page?: number; limit?: number }) {
    return api.get<ApiResponse<ClinicVisit[]> & { meta: PaginationMeta }>(
      `/clinic/students/${studentId}/visits`,
      { params: query },
    );
  },

  getStudentMedications(studentId: string, query?: { page?: number; limit?: number }) {
    return api.get<ApiResponse<MedicationAdministration[]> & { meta: PaginationMeta }>(
      `/clinic/students/${studentId}/medications`,
      { params: query },
    );
  },

  // ── Medication Administration ──────────────────────────────────────────────

  listMedications(query?: MedicationListQuery) {
    return api.get<ApiResponse<MedicationAdministration[]> & { meta: PaginationMeta }>(
      '/clinic/medications',
      { params: query },
    );
  },

  createMedication(data: CreateMedicationRequest) {
    return api.post<ApiResponse<MedicationAdministration>>('/clinic/medications', data);
  },

  // ── Inventory ──────────────────────────────────────────────────────────────

  listInventory(query?: InventoryListQuery) {
    return api.get<ApiResponse<InventoryItem[]> & { meta: PaginationMeta }>(
      '/clinic/inventory',
      { params: query },
    );
  },

  createInventoryItem(data: CreateInventoryItemRequest) {
    return api.post<ApiResponse<InventoryItem>>('/clinic/inventory', data);
  },

  updateInventoryItem(id: string, data: Partial<CreateInventoryItemRequest>) {
    return api.put<ApiResponse<InventoryItem>>(`/clinic/inventory/${id}`, data);
  },

  listTransactions(query?: TransactionListQuery) {
    return api.get<ApiResponse<InventoryTransaction[]> & { meta: PaginationMeta }>(
      '/clinic/inventory/transactions',
      { params: query },
    );
  },

  createTransaction(data: CreateTransactionRequest) {
    return api.post<ApiResponse<InventoryTransaction>>('/clinic/inventory/transactions', data);
  },

  bulkUploadInventory(file: File, clinicId?: string) {
    const formData = new FormData();
    formData.append('file', file);
    if (clinicId) {
      formData.append('clinic_id', clinicId);
    }
    return api.post<ApiResponse<BulkUploadResult>>('/clinic/inventory/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  exportInventory(format: 'csv' | 'json' = 'csv', clinicId?: string) {
    return api.get('/clinic/inventory/export', {
      params: { format, clinic_id: clinicId },
      responseType: format === 'csv' ? 'blob' : 'json',
    });
  },

  // ── Equipment ──────────────────────────────────────────────────────────────

  listEquipment(query?: EquipmentListQuery) {
    return api.get<ApiResponse<MedicalEquipment[]> & { meta: PaginationMeta }>(
      '/clinic/equipment',
      { params: query },
    );
  },

  createEquipment(data: CreateEquipmentRequest) {
    return api.post<ApiResponse<MedicalEquipment>>('/clinic/equipment', data);
  },

  // Clinic ↔ EAM Integration Pack PR 3B
  listEligibleAssets(query: { clinic_id: string; medical_only?: boolean; search?: string; page?: number; limit?: number }) {
    return api.get<ApiResponse<EligibleAssetRow[]> & { meta: PaginationMeta }>(
      '/clinic/equipment/eligible-assets',
      { params: query },
    );
  },

  linkEquipment(data: { clinic_id: string; asset_id: string }) {
    return api.post<ApiResponse<MedicalEquipment>>('/clinic/equipment/link', data);
  },

  // Clinic ↔ EAM Integration Pack PR 3C — transactional create.
  // Creates an EAM asset + clinic overlay in one request.
  createLinkedEquipment(data: CreateLinkedEquipmentRequest) {
    return api.post<ApiResponse<{ asset: { id: string; name: string; asset_code: string; status: string }; equipment: MedicalEquipment }>>(
      '/clinic/equipment/create-linked',
      data,
    );
  },

  updateEquipment(id: string, data: Partial<CreateEquipmentRequest>) {
    return api.put<ApiResponse<MedicalEquipment>>(`/clinic/equipment/${id}`, data);
  },

  listChecks(query?: CheckListQuery) {
    return api.get<ApiResponse<EquipmentCheck[]> & { meta: PaginationMeta }>(
      '/clinic/equipment/checks',
      { params: query },
    );
  },

  createCheck(data: CreateCheckRequest) {
    return api.post<ApiResponse<EquipmentCheck>>('/clinic/equipment/checks', data);
  },

  // ── Waste ──────────────────────────────────────────────────────────────────

  listWaste(query?: WasteListQuery) {
    return api.get<ApiResponse<WasteCollection[]> & { meta: PaginationMeta }>(
      '/clinic/waste',
      { params: query },
    );
  },

  createWaste(data: CreateWasteRequest) {
    return api.post<ApiResponse<WasteCollection>>('/clinic/waste', data);
  },

  // ── Reports ────────────────────────────────────────────────────────────────

  listReports(query?: SummaryListQuery) {
    return api.get<ApiResponse<ActivitySummary[]> & { meta: PaginationMeta }>(
      '/clinic/reports',
      { params: query },
    );
  },

  getDailyLog(query?: DailyLogQuery) {
    return api.get<ApiResponse<DailyLogEntry[]>>(
      '/clinic/reports/daily-log',
      { params: query },
    );
  },

  generateReport(data: GenerateReportRequest) {
    return api.post<ApiResponse<ActivitySummary>>('/clinic/reports/generate', data);
  },

  approveReport(id: string) {
    return api.post<ApiResponse<ActivitySummary>>(`/clinic/reports/${id}/approve`);
  },

  // ══════════════════════════════════════════════════════════════════════════
  // SoR Encounters (Phase 1+)
  // ══════════════════════════════════════════════════════════════════════════

  // ── Encounters ──────────────────────────────────────────────────────────

  listEncounters(query?: EncounterListQuery) {
    return api.get<ApiResponse<ClinicEncounter[]> & { total: number }>(
      '/clinic/encounters',
      { params: query },
    );
  },

  getEncounter(id: string) {
    return api.get<ApiResponse<ClinicEncounterDetail>>(`/clinic/encounters/${id}`);
  },

  createEncounter(data: CreateEncounterRequest) {
    return api.post<ApiResponse<ClinicEncounter>>('/clinic/encounters', data);
  },

  triageEncounter(id: string, data: TriageEncounterRequest, idempotencyKey: string) {
    return api.post<ApiResponse<void>>(`/clinic/encounters/${id}/triage`, data, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  },

  closeEncounter(id: string, data: CloseEncounterRequest) {
    return api.post<ApiResponse<void>>(`/clinic/encounters/${id}/close`, data);
  },

  submitEncounterToHIE(id: string) {
    return api.post<ApiResponse<HieSubmissionResult>>(`/clinic/encounters/${id}/submit-hie`);
  },

  async lookupByEmiratesId(cardNumber: string) {
    // Hash the Emirates ID on the client side (SHA-256)
    const encoder = new TextEncoder();
    const data = encoder.encode(cardNumber);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return api.get<ApiResponse<{ id: string; person_id: string; display_name_snapshot: string } | null>>(
      '/clinic/patient-identity/lookup-by-eid',
      { params: { emirates_id_hash: hashHex } }
    );
  },

  recordVitalsBatch(id: string, data: RecordVitalsBatchRequest, idempotencyKey: string) {
    return api.post<ApiResponse<{ ids: string[] }>>(`/clinic/encounters/${id}/vitals`, data, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  },

  // ── Clinical Data ───────────────────────────────────────────────────────

  createObservation(data: CreateObservationRequest) {
    return api.post<ApiResponse<{ id: string }>>('/clinic/observations', data);
  },

  createCondition(data: CreateConditionRequest) {
    return api.post<ApiResponse<{ id: string }>>('/clinic/conditions', data);
  },

  administerMedication(id: string, data: AdministerMedicationRequest, idempotencyKey: string) {
    return api.post<ApiResponse<{ id: string; safety_result: SafetyCheckResult }>>(`/clinic/encounters/${id}/medications`, data, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  },

  runSafetyCheck(data: SafetyCheckRequest) {
    return api.post<ApiResponse<SafetyCheckResult>>('/clinic/medication-safety-check', data);
  },

  administerImmunization(data: AdministerImmunizationRequest) {
    return api.post<ApiResponse<{ id: string }>>('/clinic/immunizations', data);
  },

  // ── Referrals ───────────────────────────────────────────────────────────

  createReferral(data: CreateReferralRequest) {
    return api.post<ApiResponse<{ id: string }>>('/clinic/referrals', data);
  },

  updateReferral(id: string, data: UpdateReferralRequest) {
    return api.put<ApiResponse<void>>(`/clinic/referrals/${id}`, data);
  },

  // ── Consents ────────────────────────────────────────────────────────────

  createConsent(data: CreateConsentRequest) {
    return api.post<ApiResponse<{ id: string }>>('/clinic/consents', data);
  },

  revokeConsent(id: string, data: RevokeConsentRequest) {
    return api.post<ApiResponse<void>>(`/clinic/consents/${id}/revoke`, data);
  },

  // ── Incident Linkage (Phase 1.5) ────────────────────────────────────────

  createIncidentFromEncounter(encounterId: string, data: CreateIncidentFromEncounterRequest) {
    return api.post<ApiResponse<{ incident_id: string; incident_number: string }>>(`/clinic/encounters/${encounterId}/incident`, data);
  },

  linkEncounterToIncident(encounterId: string, data: LinkEncounterToIncidentRequest) {
    return api.post<ApiResponse<void>>(`/clinic/encounters/${encounterId}/incident/link`, data);
  },

  unlinkEncounterFromIncident(encounterId: string) {
    return api.delete<ApiResponse<void>>(`/clinic/encounters/${encounterId}/incident`);
  },

  // ── Auto-Muster Alerts (Phase 1.6) ──────────────────────────────────────

  listAutoMusterAlerts(query?: AutoMusterAlertListQuery) {
    return api.get<ApiResponse<AutoMusterAlert[]> & { total: number; server_now: string }>(
      '/clinic/auto-muster-alerts',
      { params: query },
    );
  },

  acknowledgeAlert(id: string, idempotencyKey: string) {
    return api.post<ApiResponse<void>>(`/clinic/auto-muster-alerts/${id}/acknowledge`, {}, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  },

  startAlertResponse(id: string) {
    return api.post<ApiResponse<void>>(`/clinic/auto-muster-alerts/${id}/start-response`, {});
  },

  resolveAlert(id: string, data: ResolveAlertRequest, idempotencyKey: string) {
    return api.post<ApiResponse<void>>(`/clinic/auto-muster-alerts/${id}/resolve`, data, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  },

  markAlertFalseAlarm(id: string, data: MarkFalseAlarmRequest, idempotencyKey: string) {
    return api.post<ApiResponse<void>>(`/clinic/auto-muster-alerts/${id}/false-alarm`, data, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  },

  // ── Staff ───────────────────────────────────────────────────────────────

  getClinicStaff(clinicId: string) {
    return api.get<ApiResponse<ClinicStaffMember[]>>(`/clinic/clinics/${clinicId}/staff`);
  },

  addStaff(clinicId: string, data: { user_id: string; staff_role?: string }) {
    return api.post<ApiResponse<ClinicStaffMember>>(`/clinic/clinics/${clinicId}/staff`, data);
  },

  removeStaff(clinicId: string, userId: string) {
    return api.delete<ApiResponse<void>>(`/clinic/clinics/${clinicId}/staff/${userId}`);
  },

  // ── Staff Documents ────────────────────────────────────────────────────

  getStaffDocumentTypes() {
    return api.get<ApiResponse<StaffDocumentType[]>>('/clinic/staff-document-types');
  },

  getStaffDocuments(clinicId: string, userId: string, versions: 'current' | 'all' = 'current') {
    return api.get<ApiResponse<StaffDocument[]>>(
      `/clinic/clinics/${clinicId}/staff/${userId}/documents`,
      { params: versions === 'all' ? { versions: 'all' } : undefined },
    );
  },

  uploadStaffDocument(clinicId: string, userId: string, data: UploadStaffDocumentRequest) {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('document_type_code', data.document_type_code);
    if (data.valid_until) {
      formData.append('valid_until', data.valid_until);
    }
    // Let the browser set Content-Type with correct multipart boundary
    return api.post<ApiResponse<StaffDocument>>(
      `/clinic/clinics/${clinicId}/staff/${userId}/documents`,
      formData,
    );
  },

  downloadStaffDocument(clinicId: string, userId: string, documentId: string) {
    return api.get<Blob>(
      `/clinic/clinics/${clinicId}/staff/${userId}/documents/${documentId}/download`,
      { responseType: 'blob' },
    );
  },

  verifyStaffDocument(clinicId: string, userId: string, documentId: string, data: VerifyStaffDocumentRequest) {
    return api.patch<ApiResponse<StaffDocument>>(
      `/clinic/clinics/${clinicId}/staff/${userId}/documents/${documentId}`,
      data,
    );
  },

  deleteStaffDocument(clinicId: string, userId: string, documentId: string) {
    return api.delete<ApiResponse<void>>(
      `/clinic/clinics/${clinicId}/staff/${userId}/documents/${documentId}`,
    );
  },

  // ── Lookup (ICD-10 / LOINC) ─────────────────────────────────────────────

  searchIcd10(search: string, limit = 20) {
    return api.get<ApiResponse<Icd10Entry[]>>('/clinic/lookup/icd10', { params: { search, limit } });
  },

  searchLoinc(search: string, limit = 20) {
    return api.get<ApiResponse<LoincEntry[]>>('/clinic/lookup/loinc', { params: { search, limit } });
  },

  // ── Appointments ──────────────────────────────────────────────────────────

  listAppointments(query?: AppointmentListQuery) {
    return api.get<ApiResponse<Appointment[]> & { meta: PaginationMeta }>(
      '/clinic/appointments',
      { params: query },
    );
  },

  createAppointment(data: CreateAppointmentRequest) {
    return api.post<ApiResponse<Appointment>>('/clinic/appointments', data);
  },

  confirmAppointment(id: string) {
    return api.post<ApiResponse<Appointment>>(`/clinic/appointments/${id}/confirm`);
  },

  arriveAppointment(id: string) {
    return api.post<ApiResponse<{ appointment: Appointment; encounter: { id: string; encounter_number: string } }>>(`/clinic/appointments/${id}/arrive`);
  },

  cancelAppointment(id: string, data: { reason: string }) {
    return api.post<ApiResponse<Appointment>>(`/clinic/appointments/${id}/cancel`, data);
  },

  noShowAppointment(id: string) {
    return api.post<ApiResponse<Appointment>>(`/clinic/appointments/${id}/no-show`);
  },

  // ── Queue ─────────────────────────────────────────────────────────────────

  getQueue(query?: QueueListQuery) {
    return api.get<ApiResponse<QueueEntry[]>>('/clinic/queue', { params: query });
  },

  // ── Create Clinic ─────────────────────────────────────────────────────────

  createClinic(data: CreateClinicRequest) {
    return api.post<ApiResponse<ClinicListItem>>('/clinic/clinics', data);
  },

  updateClinic(id: string, data: UpdateClinicRequest) {
    return api.put<ApiResponse<ClinicListItem>>(`/clinic/clinics/${id}`, data);
  },
};
